import type { ConsoleNotificationWsPayload } from '@/lib/notifications';

function resolveWebSocketUrl(): string {
  const configured = import.meta.env.VITE_WS_URL as string | undefined;
  if (!configured) return 'ws://localhost:5311/ws';
  if (configured.startsWith('/')) {
    if (typeof globalThis.window === 'undefined') return configured;
    const protocol = globalThis.window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${globalThis.window.location.host}${configured}`;
  }
  return configured;
}

const WS_URL = resolveWebSocketUrl();

type NotificationHandler = (payload: ConsoleNotificationWsPayload) => void;
type ChannelHandler = (payload: unknown) => void;

let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let connectionKey: string | null = null;
let notificationUserId: string | null = null;
const notificationHandlers = new Set<NotificationHandler>();
const channelHandlers = new Map<string, Set<ChannelHandler>>();
const subscribedChannels = new Set<string>();

const notifyNotifications = (payload: ConsoleNotificationWsPayload) => {
  for (const handler of notificationHandlers) handler(payload);
};

const notifyChannel = (channel: string, payload: unknown) => {
  const handlers = channelHandlers.get(channel);
  if (!handlers) return;
  for (const handler of handlers) handler(payload);
};

const clearReconnect = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const scheduleReconnect = (connect: () => void) => {
  clearReconnect();
  reconnectTimer = setTimeout(connect, 3_000);
};

const sendSubscribe = (channel: string) => {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify({ type: 'subscribe', channel }));
};

const subscribeSocketChannel = (channel: string) => {
  subscribedChannels.add(channel);
  sendSubscribe(channel);
};

const unsubscribeSocketChannel = (channel: string) => {
  subscribedChannels.delete(channel);
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify({ type: 'unsubscribe', channel }));
};

const subscribeAllChannels = () => {
  if (notificationUserId) {
    sendSubscribe(`notifications:console:user:${notificationUserId}`);
  }
  for (const channel of subscribedChannels) {
    sendSubscribe(channel);
  }
};

export const subscribeConsoleNotificationEvents = (handler: NotificationHandler): (() => void) => {
  notificationHandlers.add(handler);
  return () => notificationHandlers.delete(handler);
};

export const subscribeWebSocketChannel = (
  channel: string,
  handler: ChannelHandler,
): (() => void) => {
  const handlers = channelHandlers.get(channel) ?? new Set<ChannelHandler>();
  handlers.add(handler);
  channelHandlers.set(channel, handlers);
  subscribeSocketChannel(channel);

  return () => {
    const current = channelHandlers.get(channel);
    if (!current) return;
    current.delete(handler);
    if (current.size === 0) {
      channelHandlers.delete(channel);
      unsubscribeSocketChannel(channel);
    }
  };
};

export const disconnectConsoleWebSocket = (): void => {
  clearReconnect();
  connectionKey = null;
  notificationUserId = null;
  subscribedChannels.clear();
  channelHandlers.clear();
  if (socket) {
    socket.onclose = null;
    socket.close();
    socket = null;
  }
};

/** Cookie-authenticated console socket; church id travels in the query string. */
export const connectConsoleWebSocket = (input: {
  churchId: string;
  userId: string;
}): (() => void) => {
  const key = `${input.churchId}:${input.userId}`;
  if (connectionKey === key && socket?.readyState === WebSocket.OPEN) {
    return disconnectConsoleWebSocket;
  }

  disconnectConsoleWebSocket();
  connectionKey = key;
  notificationUserId = input.userId;

  const open = () => {
    if (connectionKey !== key) return;

    const url = new URL(WS_URL);
    url.searchParams.set('churchId', input.churchId);

    const next = new WebSocket(url.toString());
    socket = next;

    next.onopen = () => {
      subscribeAllChannels();
    };

    next.onmessage = (event) => {
      let message: { type?: string; channel?: string; payload?: unknown };
      try {
        message = JSON.parse(String(event.data)) as {
          type?: string;
          channel?: string;
          payload?: unknown;
        };
      } catch {
        return;
      }

      if (message.type !== 'event' || !message.payload || typeof message.payload !== 'object') {
        return;
      }

      if (typeof message.channel === 'string') {
        notifyChannel(message.channel, message.payload);
      }

      if (
        message.channel === `notifications:console:user:${input.userId}`
      ) {
        notifyNotifications(message.payload as ConsoleNotificationWsPayload);
      }
    };

    next.onclose = () => {
      if (connectionKey !== key) return;
      socket = null;
      scheduleReconnect(open);
    };
  };

  open();

  return () => {
    if (connectionKey === key) disconnectConsoleWebSocket();
  };
};
